'use client'

import { track } from '@amplitude/analytics-browser'

import { Link } from '@/i18n/navigation'

export type InteractedEventName = 'Homepage Interacted' | 'Landing Page Interacted'

/**
 * Cited mirror of the ratified page-interaction contract in
 * `strategy/.docs/analytics/amplitude-events-content-pages.md` — types only
 * the interaction types nexus fires, keyed on `interaction_type` so each
 * payload's required props are checked at compile time. The dictionary is the
 * source of truth; sync this union when its changelog touches these events.
 */
export type InteractionProperties =
    | {
          interaction_type: 'route_clicked'
          ui_source: 'featured_route_card' | 'plan_route_list'
          route_url: string
          position: number
          mode: string
      }
    | { interaction_type: 'carrier_announcement_clicked'; carrier_slug: string; link_url: string }
    | {
          interaction_type: 'carrier_clicked'
          carrier_slug: string
          position: number
          link_url: string
      }
    | { interaction_type: 'promotion_clicked'; promotion_name: string; link_url: string }
    | {
          interaction_type: 'content_link_clicked'
          content_name: string
          link_url: string
          position?: number
          is_outbound?: boolean
      }
    | { interaction_type: 'destination_toggled'; destination: string; expanded: boolean }
    | {
          interaction_type: 'app_download_clicked'
          store: 'app_store' | 'play_store'
          is_outbound: true
      }
    | {
          interaction_type: 'outbound_link_clicked'
          link_url: string
          link_domain: string
          is_outbound: true
      }

/**
 * Fires a page-interaction Amplitude event against the typed contract.
 * Amplitude-only by design — these interaction events have no Snowplow
 * counterpart (PR #488 review). `page_type` is added by the page-type
 * enrichment plugin. Non-link interactions (e.g. the plan accordion's
 * `destination_toggled`) call this directly; links use `InteractionLink`.
 */
export function trackInteraction(
    eventName: InteractedEventName,
    properties: InteractionProperties,
): void {
    track(eventName, properties)
}

export interface InteractionLinkProps {
    href: string
    /** The interaction payload sent verbatim on the Amplitude event. */
    properties: InteractionProperties
    /**
     * Amplitude event to fire. Shared components rendered on landing pages
     * pass `'Landing Page Interacted'`; the two events share one
     * `interaction_type` contract but must never fire on each other's pages.
     */
    eventName?: InteractedEventName
    /** Render a plain <a> (absolute/off-app URLs); locale-aware Link otherwise. */
    external?: boolean
    target?: string
    rel?: string
    'aria-label'?: string
    className?: string
    children: React.ReactNode
}

/**
 * Client island wrapping a clickable on an interaction-tracked page
 * (homepage or landing) so the click fires the ratified `… Interacted`
 * Amplitude event. Keeps the section components server-rendered — only the
 * link is interactive.
 */
export const InteractionLink = ({
    href,
    properties,
    eventName = 'Homepage Interacted',
    external = false,
    target,
    rel,
    'aria-label': ariaLabel,
    className,
    children,
}: InteractionLinkProps) => {
    const handleClick = () => {
        trackInteraction(eventName, properties)
    }

    if (external) {
        return (
            <a
                href={href}
                target={target}
                rel={rel}
                aria-label={ariaLabel}
                className={className}
                onClick={handleClick}
            >
                {children}
            </a>
        )
    }

    return (
        <Link href={href} aria-label={ariaLabel} className={className} onClick={handleClick}>
            {children}
        </Link>
    )
}
